import { runInAction, set } from 'mobx';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { OmniCampaign } from '@feathr/blackbox';
import { EIntegrationTypes } from '@feathr/blackbox';
import type { IAudienceBreakdownReturn } from '@feathr/report_components';

import {
  calculateChannelBudgets,
  calculateRecommendedSplit,
  getAllocationPercentage,
} from './ChannelBudgetSplit.utils';

/** Params for `useChannelBudgetSplit`. */
interface IUseChannelBudgetSplitParams {
  /** The campaign whose exposure_settings hold the persisted channel split. */
  campaign: OmniCampaign;
  /** Audience breakdown data used to compute reach and recommended split. */
  audienceData: IAudienceBreakdownReturn;
}

/** Return value of `useChannelBudgetSplit`. */
interface IUseChannelBudgetSplitReturn {
  /** Current slider value (display channel percentage). */
  displayPercent: number;
  /** Live slider update callback (before persistence). */
  setDisplayPercent: (value: number) => void;
  /** Dollar amount allocated to the display channel. */
  displayBudget: number;
  /** Dollar amount allocated to the meta channel. */
  metaBudget: number;
  /** Whether to render the audience-drift warning Alert. */
  showAudienceDriftWarning: boolean;
  /** Persist the slider value on release. */
  handleSliderChangeEnd: (value: number) => void;
  /** Reset the slider to the recommended split and persist. */
  handleUseRecommended: () => void;
  /** Audience-driven recommended display percentage. */
  recommendedDisplayPercent: number;
}

/**
 * Encapsulates all state management, effects, and MobX persistence logic
 * for the ChannelBudgetSplit component.
 *
 * @param params - Campaign and audience data.
 * @returns Values and handlers consumed by the presentation component.
 */
export function useChannelBudgetSplit({
  campaign,
  audienceData,
}: IUseChannelBudgetSplitParams): IUseChannelBudgetSplitReturn {
  /* Lookalike audiences are served via the Display (TTD) channel. */
  const displayReach =
    (audienceData.numTargetedReachableCookies ?? 0) +
    (audienceData.targetedLookalikeAudienceSize ?? 0);
  const metaReach = audienceData.numTargetedReachableEmails ?? 0;

  const recommended = useMemo(
    () => calculateRecommendedSplit(displayReach, metaReach),
    [displayReach, metaReach],
  );

  const persistedSplit = getAllocationPercentage(
    campaign.get('exposure_settings')?.channel_split?.allocations,
    EIntegrationTypes.Tradedesk,
  );

  const initialDisplay = persistedSplit ?? recommended.tradedesk;
  const [displayPercent, setDisplayPercent] = useState<number>(initialDisplay);

  /*
   * Track whether the user has manually adjusted the slider.
   * Once the user interacts, we stop syncing from audience data.
   */
  const hasUserAdjustedRef = useRef(persistedSplit !== undefined);

  /*
   * When audience data loads asynchronously, re-sync the slider to the
   * recommended split — but only if the user hasn't persisted a value
   * and hasn't manually adjusted the slider during this session.
   */
  useEffect(() => {
    if (persistedSplit === undefined && !hasUserAdjustedRef.current) {
      setDisplayPercent(recommended.tradedesk);
    }
  }, [recommended.tradedesk, persistedSplit, displayReach, metaReach]);

  const totalBudget = campaign.get('exposure_settings')?.target_value ?? 0;
  const { displayBudget, metaBudget } = calculateChannelBudgets(totalBudget, displayPercent);

  /*
   * TODO: Revisit audience drift warning with a better signal — detect
   * audience *selection* changes rather than reach-number drift, which
   * produces false positives on page load for returning users.
   */
  const showAudienceDriftWarning = false;

  /** Write the given display percentage to the campaign's exposure_settings via MobX. */
  function persistSplit(tradedesk: number): void {
    const exposureSettings = campaign.get('exposure_settings');
    if (!exposureSettings) {
      return;
    }
    runInAction(() => {
      set(exposureSettings, {
        channel_split: {
          allocations: [
            { channel: EIntegrationTypes.Tradedesk, percentage: tradedesk },
            { channel: EIntegrationTypes.Meta, percentage: 100 - tradedesk },
          ],
        },
      });
      campaign.setAttributeDirty('exposure_settings');
    });
  }

  function handleSliderChangeEnd(value: number): void {
    hasUserAdjustedRef.current = true;
    persistSplit(value);
  }

  function handleUseRecommended(): void {
    setDisplayPercent(recommended.tradedesk);
    persistSplit(recommended.tradedesk);
  }

  return {
    displayPercent,
    setDisplayPercent,
    displayBudget,
    metaBudget,
    showAudienceDriftWarning,
    handleSliderChangeEnd,
    handleUseRecommended,
    recommendedDisplayPercent: recommended.tradedesk,
  };
}
